import { useMemo } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { ProgressTooltip } from './progress-tooltip';
import { TextSize, Typography } from '@shared/index';

const CIRCLE_SIZE = 238;
const INNER_RADIUS = 92;
const OUTER_RADIUS = 100;
const INNER_DIAMETER = INNER_RADIUS * 2;
const PROGRESS_BAR_CENTER_RADIUS = INNER_RADIUS + (OUTER_RADIUS - INNER_RADIUS) / 2;

type Props = {
  percent: number;
  currentValue: number;
  plan: number;
  remainingPercent: number;
  remainingPh: string;
  progressColor?: string;
  indicatorColor?: string;
};

const DEFAULT_PROGRESS_COLOR = '#AACBDD';
const DEFAULT_INDICATOR_COLOR = '#3BB5F7';
const REMAINING_COLOR = '#2E2E2E';

export const ProgressGraph = ({
  percent,
  currentValue,
  remainingPercent,
  remainingPh,
  progressColor = DEFAULT_PROGRESS_COLOR,
  indicatorColor = DEFAULT_INDICATOR_COLOR,
}: Props) => {
  const chartData = useMemo(() => {
    const progress = Math.min(100, Math.max(0, percent));
    return [
      { name: 'progress', value: progress },
      { name: 'remaining', value: Math.max(0, 100 - progress) },
    ];
  }, [percent]);

  const displayPercent = percent >= 100 ? Math.round(percent).toString() : percent.toFixed(0);
  const displayValue = currentValue.toLocaleString();

  const indicatorPosition = useMemo(() => {
    if (percent <= 0) return null;

    const progressAngle = 90 - (percent / 100) * 360;
    const angleRad = (progressAngle * Math.PI) / 180;

    return {
      x: CIRCLE_SIZE / 2 + PROGRESS_BAR_CENTER_RADIUS * Math.cos(angleRad),
      y: CIRCLE_SIZE / 2 - PROGRESS_BAR_CENTER_RADIUS * Math.sin(angleRad),
    };
  }, [percent]);

  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <div className="relative">
      <PieChart width={CIRCLE_SIZE} height={CIRCLE_SIZE}>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={INNER_RADIUS}
          outerRadius={OUTER_RADIUS}
          startAngle={90}
          endAngle={-270}
          dataKey="value"
          stroke="none"
        >
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.name === 'progress' ? progressColor : REMAINING_COLOR}
            />
          ))}
        </Pie>
      </PieChart>
      <div
        className="absolute top-1/2 left-1/2 rounded-full"
        style={{
          width: `${INNER_DIAMETER}px`,
          height: `${INNER_DIAMETER}px`,
          backgroundColor: '#1E1E1E',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-center">
        <Typography text={`${displayValue} Ph`} size={TextSize.size24} className="font-medium" />
        <Typography
          text={`${displayPercent}%`}
          size={TextSize.size20}
          className="font-medium text-gray-100"
        />
      </div>
      {indicatorPosition && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: `${indicatorPosition.x}px`,
            top: `${indicatorPosition.y}px`,
            transform: 'translate(-50%, -50%)',
            zIndex: 15,
          }}
        >
          <div
            className="absolute rounded-full"
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: progressColor,
              boxShadow: `0 0 12px ${hexToRgba(progressColor, 0.8)}, 0 0 6px ${hexToRgba(progressColor, 0.4)}`,
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              width: '10px',
              height: '10px',
              backgroundColor: indicatorColor,
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        </div>
      )}
      <ProgressTooltip
        indicatorPosition={indicatorPosition}
        remainingPercent={remainingPercent}
        remainingPh={remainingPh}
      />
    </div>
  );
};
